<template>
  <div class="media-and-text-item">
    <el-form ref="form" :model="form" :rules="rules" label-width="80px" label-position="left">
      <el-form-item :label="$t('microSite.common.form.clickToJump')" prop='clickLink'>
        <SwitchButton v-model="form.clickLink.isActive" :options="clickLinkOptions"></SwitchButton>
        <template v-if="form.clickLink.isActive" >
          <image-link :link="form.clickLink"></image-link>
        </template>
      </el-form-item>
      <el-form-item :label="$t('microSite.common.form.upload')" prop='media'>
        <SwitchButton v-model="form.media.isImage" :options="mediaTypeOptions"></SwitchButton>
        <div v-if="form.media.isImage" class="upload-image">
          <ImageUpload v-model="form.media.src" :otherTips="uploadTip"></ImageUpload>
        </div>
        <div v-else class="upload-video">
          <VideoUpload v-model="form.media" :otherTips="uploadTip"></VideoUpload>
        </div>
      </el-form-item>
      <el-form-item :label="$t('microSite.common.form.align')">
        <el-select v-model="form.align">
          <el-option v-for="item in alignList" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('microSite.common.form.title')">
        <el-input v-model="form.title" :placeholder="$t('microSite.common.form.titlePlaceholder')"></el-input>
      </el-form-item>
      <el-form-item :label="$t('microSite.common.form.content')">
        <quill-editor ref="quillEditor" v-model="form.content" :options="quillEditorOption"></quill-editor>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import editorTemplate from '../../editorTemplate'
import { microSiteCommonOption } from '@/utils/quillEditor'
import SwitchButton from '../../../components/editor/switchButton.vue'
import ImageUpload from '../../../components/editor/imageUpload.vue'
import VideoUpload from '../../../components/editor/videoUpload.vue'
import ImageLink from '../../../components/link/index.vue'
import { suggestionUploadSize } from '../index'
import { CHILD_EDITOR_NAME } from '../constant'
import Quill from 'quill'
import _ from 'lodash'

export default {
  name: CHILD_EDITOR_NAME,
  components: {
    SwitchButton,
    ImageUpload,
    VideoUpload,
    ImageLink
  },
  mixins: [editorTemplate],
  data () {
    const checkUpload = (rule, value, callback) => {
      if (!value.src) {
        return callback(new Error(this.$t('microSite.common.form.uploadCanNotEmpty')))
      }
      callback()
    }
    const checkLink = (rule, value, callback) => {
      if (value.isActive && !value.url) {
        return callback(new Error(this.$t('microSite.common.form.linkCanNotEmpty')))
      }
      callback()
    }
    return {
      quillEditorOption: microSiteCommonOption,
      form: {
        title: null,
        content: null,
        align: 'align-top',
        media: {
          isImage: true,
          src: null,
          coverImage: null
        },
        clickLink: {
          isActive: false,
          isInnerPage: true,
          url: null,
          openNewWindow: false
        }
      },
      rules: {
        media: [
          { validator: checkUpload, trigger: 'blur' }
        ],
        clickLink: [
          { validator: checkLink, trigger: 'blur' }
        ]
      },
      clickLinkOptions: [
        {
          labelI18nKey: 'microSite.common.form.no',
          value: false
        },
        {
          labelI18nKey: 'microSite.common.form.yes',
          value: true
        }
      ],
      mediaTypeOptions: [
        {
          label: this.$t('microSite.common.form.img'),
          value: true
        },
        {
          label: this.$t('microSite.common.form.video'),
          value: false
        }
      ],
      allInnerPages: [],
      alignList: [
        {
          label: this.$t('microSite.mediaAndText.label.alignTop'),
          value: 'align-top'
        },
        {
          label: this.$t('microSite.mediaAndText.label.alignCenter'),
          value: 'align-center'
        },
        {
          label: this.$t('microSite.mediaAndText.label.alignBottom'),
          value: 'align-bottom'
        }
      ]
    }
  },
  computed: {
    uploadTip () {
      const renderMode = this.renderData.parent.data.props.renderMode
      const size = suggestionUploadSize[this.isMobile ? 'mobile' : 'pc'][renderMode]
      if (size) {
        return this.$t('microSite.component.upload.suggestion', { size: size })
      } else {
        return ''
      }
    }
  },
  created () {
    const props = this.renderData.data.props
    this.form.title = props.title
    this.form.align = props.align || 'align-top'
    this.form.content = props.content
    this.form.media = {
      ...props.media
    }
    this.form.media.coverImage = props.media.coverImage || ''
    this.form.clickLink = {
      ...props.clickLink
    }
    const alignAttributor = Quill.import('attributors/class/align')
    alignAttributor.whitelist = [
      ...alignAttributor.whitelist,
      'left'
    ]
    Quill.register(alignAttributor, true)
    const fontSizeStyle = Quill.import('attributors/style/size')
    fontSizeStyle.whitelist = ['8px', '10px', '12px', '14px', '16px', '20px', '24px', '36px']
    Quill.register(fontSizeStyle, true)
  },
  mounted () {
    const quill = this.$refs.quillEditor.quill
    quill.getModule('toolbar').addHandler('align', function (value) {
      this.quill.format('align', value || 'left')
    })
    const targetSize = '12px'
    quill.format('size', targetSize)
    const toolbar = quill.getModule('toolbar')
    const sizePicker = toolbar.container.querySelector('.ql-size')
    if (sizePicker) {
      _.forEach(sizePicker.querySelectorAll('.ql-picker-item'), item => {
        item.classList.remove('ql-selected')
      })
      const targetItem = sizePicker.querySelector(`[data-value="${targetSize}"]`)
      if (targetItem) {
        targetItem.classList.add('ql-selected')
        const label = sizePicker.querySelector('.ql-picker-label')
        if (label) {
          label.setAttribute('data-value', targetSize)
        }
      }
    }
  },
  methods: {
    validateAndUpdateRenderData () {
      let result = false
      this.$refs.form.validate(valid => { result = valid })
      if (!result) {
        return result
      }
      const props = this.renderData.data.props
      props.align = this.form.align
      props.title = this.form.title
      props.content = this.form.content
      props.media.isImage = this.form.media.isImage
      props.media.src = this.form.media.src
      props.media.coverImage = this.form.media.coverImage
      props.clickLink = { ...this.form.clickLink, url: this.form.clickLink.url && this.form.clickLink.url.trim() }

      this.updateConfigInRoot()
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.media-and-text-item {
  .link-container {
    margin-top: 10px;

    .link-type {
      width: 25%;
    }

    .link-path {
      width: 70%;
      margin-left: 5%;
    }
  }
  .click-link {
    margin-top: 22px;

    .el-form-item:not(:last-child) {
      margin-bottom: 22px;
    }
  }
  .upload-image {
    margin-top: 22px;
  }

  .upload-video {
    margin-top: 22px;
  }

  ::v-deep {
    .ql-formats {
      line-height: normal;
    }

    .ql-snow {
      .ql-size {
        .ql-picker-label,
        .ql-picker-item {
          &::before {
            content: attr(data-value);
          }
        }
      }
    }
  }
}
</style>
